import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CreditCard, DollarSign, PiggyBank, ChevronDown, ChevronRight } from 'lucide-react';

const Step6Payment = ({ paymentConnected, onUpdate, onComplete, onBack, onSkip }) => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const faqItems = [
    {
      id: 'why-payment',
      question: 'Why do I need a payment method?',
      answer: 'To receive payments from your supporters, you need to connect a payment method. This allows us to transfer funds to your account when people donate or subscribe.'
    },
    {
      id: 'change-later',
      question: 'Can I change my mind later?',
      answer: "Absolutely! You can connect or disconnect payment methods at any time from your creator dashboard. Your supporters can still donate, and you can set up payments whenever you're ready."
    },
    {
      id: 'no-account',
      question: "I don't have PayPal or Stripe.",
      answer: "No worries! You can create accounts with either service during the connection process. They're free to set up and widely trusted for online payments."
    }
  ];

  const handlePaymentSelect = (paymentType) => {
    setSelectedPayment(paymentType);
    onUpdate({ paymentConnected: true });
  };

  return (
    <StepContainer>
      <StepCard>
        <StepHeader>
          <StepTitle>Ready to start earning?</StepTitle>
          <StepSubtitle>Connect to a payment method to get started.</StepSubtitle>
          <StepDescription>
            If you are new to PayPal or Stripe, don't worry, you can set up an account when you Connect.
          </StepDescription>
        </StepHeader>

        <PaymentOptionsContainer>
          <PaymentOption
            onClick={() => handlePaymentSelect('paypal')}
            $selected={selectedPayment === 'paypal'}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <PaymentIcon $color="#0070BA">
              <CreditCard size={32} />
            </PaymentIcon>
            <PaymentContent>
              <PaymentTitle>PayPal</PaymentTitle>
              <PaymentDescription>Fast and trusted payments worldwide.</PaymentDescription>
            </PaymentContent>
            <ConnectButton $selected={selectedPayment === 'paypal'}>
              {selectedPayment === 'paypal' ? 'Connected' : 'Connect'}
            </ConnectButton>
          </PaymentOption>

          <PaymentOption
            onClick={() => handlePaymentSelect('stripe')}
            $selected={selectedPayment === 'stripe'}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <PaymentIcon $color="#6772E5">
              <DollarSign size={32} />
            </PaymentIcon>
            <PaymentContent>
              <PaymentTitle>Stripe</PaymentTitle>
              <PaymentDescription>Secure card payments, Apple Pay, Google Pay and more.</PaymentDescription>
            </PaymentContent>
            <ConnectButton $selected={selectedPayment === 'stripe'}>
              {selectedPayment === 'stripe' ? 'Connected' : 'Connect'}
            </ConnectButton>
          </PaymentOption>
        </PaymentOptionsContainer>

        <IllustrationContainer>
          <PiggyBankIllustration>
            <PiggyBank size={64} />
            <CoinDrop>💰</CoinDrop>
            <Sparkles>✨</Sparkles>
          </PiggyBankIllustration>
        </IllustrationContainer>

        <FaqContainer>
          {faqItems.map((item) => (
            <FaqItem key={item.id}>
              <FaqQuestion
                onClick={() => setExpandedFaq(expandedFaq === item.id ? null : item.id)}
              >
                <span>{item.question}</span>
                {expandedFaq === item.id ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              </FaqQuestion>
              <AnimatePresence>
                {expandedFaq === item.id && (
                  <FaqAnswer
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="answer-content">{item.answer}</div>
                  </FaqAnswer>
                )}
              </AnimatePresence>
            </FaqItem>
          ))}
        </FaqContainer>

        <SkipContainer>
          <SkipButton onClick={onSkip}>I'll try later</SkipButton>
        </SkipContainer>

        <ButtonContainer>
          <BackButton onClick={onBack} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ArrowLeft size={20} /> Back
          </BackButton>
          
          <CompleteButton
            onClick={onComplete}
            disabled={!selectedPayment}
            $disabled={!selectedPayment}
            whileHover={{ scale: selectedPayment ? 1.05 : 1 }}
            whileTap={{ scale: selectedPayment ? 0.95 : 1 }}
          >
            Complete Setup
          </CompleteButton>
        </ButtonContainer>
      </StepCard>
    </StepContainer>
  );
};

const StepContainer = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  justify-content: center;
`;

const StepCard = styled.div`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  width: 100%;
`;

const StepHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const StepTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: #ffffff;
  font-family: var(--font-heading);
  margin: 0 0 1rem 0;
`;

const StepSubtitle = styled.p`
  font-size: 1.1rem;
  color: #e0c16f;
  margin: 0 0 1rem 0;
`;

const StepDescription = styled.p`
  font-size: 1rem;
  color: #aaaaaa;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
`;

const PaymentOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const PaymentOption = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 2rem;
  border: 2px solid ${props => props.$selected ? '#e0c16f' : '#333333'};
  border-radius: 15px;
  cursor: pointer;
  background: ${props => props.$selected ? 'rgba(224, 193, 111, 0.05)' : '#1a1a1a'};
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #e0c16f;
    background: rgba(224, 193, 111, 0.1);
  }
`;

const PaymentIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 16px rgba(0,0,0,0.3);
`;

const PaymentContent = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const PaymentTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
`;

const PaymentDescription = styled.p`
  font-size: 0.9rem;
  color: #888888;
  margin: 0;
`;

const ConnectButton = styled.button`
  padding: 0.75rem 2.5rem;
  background: ${props => props.$selected ? '#28a745' : '#000000'};
  color: #ffffff;
  border: 1px solid ${props => props.$selected ? '#28a745' : '#e0c16f'};
  border-radius: 25px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
`;

const IllustrationContainer = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const PiggyBankIllustration = styled.div`
  position: relative;
  display: inline-block;
  color: #e0c16f;
`;

const CoinDrop = styled.span`
  position: absolute;
  top: -20px;
  right: -10px;
  font-size: 1.5rem;
  animation: drop 2s infinite;
  @keyframes drop { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(10px); } }
`;

const Sparkles = styled.span`
  position: absolute;
  top: 10px;
  left: -15px;
  font-size: 1.2rem;
  animation: sparkle 1.5s infinite;
  @keyframes sparkle { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; transform: scale(1.2); } }
`;

const FaqContainer = styled.div`
  margin-bottom: 2rem;
`;

const FaqItem = styled.div`
  border: 1px solid #333333;
  border-radius: 12px;
  margin-bottom: 1rem;
  overflow: hidden;
  background: #1a1a1a;
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  background: transparent;
  border: none;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover { background: #222222; }
`;

const FaqAnswer = styled(motion.div)`
  overflow: hidden;
  color: #aaaaaa;
  background: #151515;
  
  .answer-content {
    padding: 0 1.5rem 1.5rem 1.5rem;
    line-height: 1.6;
  }
`;

const SkipContainer = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const SkipButton = styled.button`
  background: none;
  border: none;
  color: #666666;
  text-decoration: underline;
  cursor: pointer;
  &:hover { color: #ffffff; }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #1a1a1a;
  color: #ffffff;
  border: 1px solid #333333;
  border-radius: 12px;
  cursor: pointer;
  &:hover { background: #222222; border-color: #e0c16f; }
`;

const CompleteButton = styled(motion.button)`
  flex: 1;
  padding: 1rem 2rem;
  background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)'};
  color: ${props => props.$disabled ? '#666666' : '#000000'};
  border: none;
  border-radius: 12px;
  font-weight: 700;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
`;

export default Step6Payment;
